import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { useReducedMotion } from "./usePwaEnv";

/**
 * `RouteTransition` — a very subtle fade/rise on route change. [task #15]
 *
 * On each `location.pathname` change the wrapper resets to a faded/lowered
 * state, then transitions back to neutral on the next frame. It uses only
 * Tailwind transition utilities (opacity + a 4px translateY) — no custom
 * keyframes, so it doesn't depend on (or modify) the global stylesheet. The
 * transform is GPU-only and brief, so it won't cause layout jank.
 *
 * Reduced-motion: `useReducedMotion` is resolved reactively; when set, the
 * wrapper renders permanently neutral (no transition class, no transform), so
 * content swaps instantly.
 *
 * Storybook-safe: `useReducedMotion` settles inside an effect; must be rendered
 * inside a router (AppShell always is — data router in-app, MemoryRouter in the
 * Storybook preview).
 */
export function RouteTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [entered, setEntered] = useState(true);

  useEffect(() => {
    if (reduced) {
      setEntered(true);
      return;
    }
    setEntered(false);
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [pathname, reduced]);

  if (reduced) return <>{children}</>;

  return (
    <div
      className={cn(
        "motion-safe:transition-[opacity,transform] motion-safe:duration-200 motion-safe:ease-out",
        entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
      )}
    >
      {children}
    </div>
  );
}
